/*
 *   File : external-import-file-upload.js
 *   Author URI : https://evoqins.com
 *   Description : External import file upload modal
 *   Version : v1.0
 */

import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Icon from "../Icon/icon";
import { CustomFilePicker } from "../Form-elements";
import QrScanner from "qr-scanner";


import style from "../../Styles/Component/upload-signature.module.scss";

import { _isFileTypeAllowed } from "../../Helper";
import { _uploadDocumentToS3 } from "../../Service/aws-service";
import APIService from "../../Service/api-service";
import { Modal } from "bootstrap";

const VALIDATE_QR_URL = "mf-services/cas/validate-qr";

const ExternalImportFileUploadModal = (props) => {

    const [uploadFileError, setUploadFileError] = useState("");
    const [fileLoader, setFileLoader] = useState(false);
    const [validateLoader, setValidateLoader] = useState(false);


    /* ---------- modal close hook (same as import-external-fund) ---------- */
    useEffect(() => {
        const modalEl = document.getElementById("external-import-file-upload");

        const handleHidden = () => {
            props.close();
        };

        modalEl.addEventListener("hidden.bs.modal", handleHidden);

        return () => {
            modalEl.removeEventListener("hidden.bs.modal", handleHidden);
        };
    }, [props.close]);


    /* ---------- file select ---------- */
    const _handleFileUpload = (value, file) => {
        setUploadFileError("");
        const fileName = value
            .replace(/^.*[\\\/]/, "")
            .replace(/[\(\)\s]/g, "_");

        const parts = fileName.split(".");
        if (parts.length <= 1) {
            setUploadFileError("No file extension found");
            return;
        }

        const extension = "." + parts.pop();

        if (!_isFileTypeAllowed(extension, [".jpeg", ".jpg", ".png"])) {
            setUploadFileError("File type should be .jpeg, .jpg or .png");
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setUploadFileError("File size should be less than 2MB");
            return;
        }

        _uploadViaAPI(fileName, file);
    };

    /* ---------- upload logic ---------- */
    const _commonUpload = (service, key, file) => {
        setUploadFileError("");
        setFileLoader(true);

        service(true, "general/file-upload", { key: `mandate/${key}` })
            .then((response) => {
                if (response.status_code !== 200) {
                    toast.error(response.message);
                    setFileLoader(false);
                    return;
                }

                _uploadDocumentToS3(response.data, key, file)
                    .then(async () => {
                        try {
                            const result = await QrScanner.scanImage(file, {
                                returnDetailedScanResult: true
                            });

                            const parsedQrData =
                                typeof result.data === 'string'
                                    ? JSON.parse(result.data)
                                    : result.data;

                            const fileUrl = response.data.view_info;
                            const reqId = sessionStorage.getItem("CAS_REQ_ID") || parsedQrData.reqId;
                            const clientRefNo = sessionStorage.getItem("CAS_CLIENT_REF_NO") || parsedQrData.clientRefNo;

                            if (!reqId || !clientRefNo) {
                                toast.error("Session expired. Please start the import again.");
                                setFileLoader(false);
                                return;
                            }

                            setFileLoader(false);
                            setValidateLoader(true);

                            const payload = { reqId, clientRefNo, qrCode: fileUrl };
                            APIService(true, VALIDATE_QR_URL, JSON.stringify(payload))
                                .then((validateResponse) => {
                                    setValidateLoader(false);
                                    if (validateResponse.status_code === 200) {
                                        const modalEl = document.getElementById("external-import-file-upload");
                                        const modalInstance = Modal.getInstance(modalEl);
                                        if (modalInstance) modalInstance.hide();
                                        toast.success(validateResponse.message || "CAS data imported successfully.");
                                        props.onValidateSuccess?.();
                                    } else {
                                        toast.error(validateResponse.message || "Validation failed.");
                                    }
                                })
                                .catch(() => {
                                    setValidateLoader(false);
                                    toast.error("Validation failed. Please try again.");
                                });

                        } catch (err) {
                            toast.error("Unable to read QR code from image");
                            setFileLoader(false);
                        }
                    })
                    .catch(() => {
                        toast.error("Upload failed");
                        setFileLoader(false);
                    });
            });
    };


    const _uploadViaAPI = (key, file) => {
        _commonUpload(APIService, key, file);
    };



    return (
        <div
            className={`modal fade ${style.e_upload_signature_modal}`}
            id="external-import-file-upload"
            tabIndex="-1"
            aria-hidden="true"
            data-bs-backdrop="true"
        >
            <div className={`modal-dialog modal-dialog-centered ${style.e_dialog}`}>
                <div className={`modal-content padding-32px-all position-relative ${style.e_content}`}>

                    {/* close icon – same pattern */}
                    <Icon
                        icon="close"
                        size={24}
                        className={`position-absolute  cursor-pointer e-modal-close-btn top-24px right-20px  `}
                        data-bs-dismiss="modal"
                    />

                    <h6 className="e-poppins-semi-bold e-font-18 color-almost-black text-center">
                        Upload downloaded QR code
                    </h6>
                    <p className="e-poppins-regular e-font-14 color-almost-black text-center mb-4">
                        Paste or upload a clear scanned copy (.jpg / .jpeg / .png)
                    </p>

                    <CustomFilePicker
                        accept=".jpeg, .jpg, .png"
                        className="e-select-file"
                        type={2}
                        pastable={true}
                        loading={fileLoader || validateLoader}
                        error={uploadFileError}
                        onSelectFile={(path, file) =>
                            _handleFileUpload(path, file)
                        }
                    />


                </div>
            </div>
        </div>
    );
};

export default ExternalImportFileUploadModal;
